import { useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../lib/api';
import type { Faq, FaqInput } from '../types';
import { MaterialSymbol } from './icons';

/**
 * Add/Edit FAQ modal ("FAQ Add Question Modal with Color Customization"
 * design): category select, chip color pickers, question + answer fields.
 * Saves via the FAQ CRUD endpoints and hands the result back to the caller.
 */

export const FAQ_CATEGORIES = ['General', 'Shipping', 'Returns', 'Product Info', 'Billing'];

/** Per-category default chip colors (from the design's palette). */
const CATEGORY_PRESETS: Record<string, { bg: string; text: string }> = {
  General: { bg: '#e0e0ff', text: '#01006e' },
  Shipping: { bg: '#ffdbce', text: '#7f2b00' },
  Returns: { bg: '#e0e0ff', text: '#1a1cdc' },
  'Product Info': { bg: '#e0e3e5', text: '#444749' },
  Billing: { bg: '#ffdbce', text: '#7f2b00' },
};

function toast(message: string, isError = false): void {
  window.shopify?.toast.show(message, isError ? { isError: true } : undefined);
}

export default function FaqModal({
  faq,
  onClose,
  onSaved,
}: {
  /** Existing entry to edit, or null to create a new one. */
  faq: Faq | null;
  onClose: () => void;
  onSaved: (faq: Faq, isNew: boolean) => void;
}): ReactNode {
  const [form, setForm] = useState<FaqInput>(
    faq
      ? {
          question: faq.question,
          answer: faq.answer,
          category: faq.category,
          categoryBgColor: faq.categoryBgColor,
          categoryTextColor: faq.categoryTextColor,
        }
      : {
          question: '',
          answer: '',
          category: 'General',
          categoryBgColor: CATEGORY_PRESETS.General!.bg,
          categoryTextColor: CATEGORY_PRESETS.General!.text,
        },
  );
  const [saving, setSaving] = useState(false);

  const onCategoryChange = (category: string): void => {
    const preset = CATEGORY_PRESETS[category];
    setForm((f) => ({
      ...f,
      category,
      ...(preset ? { categoryBgColor: preset.bg, categoryTextColor: preset.text } : {}),
    }));
  };

  const onSubmit = async (): Promise<void> => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast('Both question and answer are required', true);
      return;
    }
    setSaving(true);
    try {
      if (faq) {
        const { faq: updated } = await api.updateFaq(faq.id, form);
        toast('Question updated');
        onSaved(updated, false);
      } else {
        const { faq: created } = await api.createFaq(form);
        toast('Question added');
        onSaved(created, true);
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Failed to save question', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cg-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cg-modal"
        role="dialog"
        aria-modal="true"
        aria-label={faq ? 'Edit Question' : 'Add New Question'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cg-modal-head">
          <div className="cg-modal-head-left">
            <div className="cg-modal-icon">
              <MaterialSymbol name={faq ? 'edit' : 'add_circle'} />
            </div>
            <h2 className="cg-modal-title">{faq ? 'Edit Question' : 'Add New Question'}</h2>
          </div>
          <button type="button" className="cg-icon-btn" aria-label="Close" onClick={onClose}>
            <MaterialSymbol name="close" size={20} />
          </button>
        </div>

        <div className="cg-modal-body">
          <div>
            <label className="cg-field-label" htmlFor="faq-category">
              Category
            </label>
            <div className="cg-select-wrap">
              <select
                id="faq-category"
                className="cg-select"
                value={form.category}
                onChange={(e) => onCategoryChange(e.target.value)}
              >
                {FAQ_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <MaterialSymbol name="keyboard_arrow_down" size={20} />
            </div>
          </div>

          <div>
            <span className="cg-field-label">Style</span>
            <div className="cg-color-grid">
              <div>
                <label className="cg-color-sublabel" htmlFor="faq-bg-color">
                  Category BG Color
                </label>
                <div className="cg-color-field">
                  <input
                    id="faq-bg-color"
                    type="color"
                    value={form.categoryBgColor}
                    onChange={(e) => setForm((f) => ({ ...f, categoryBgColor: e.target.value }))}
                  />
                  <span>{form.categoryBgColor}</span>
                </div>
              </div>
              <div>
                <label className="cg-color-sublabel" htmlFor="faq-text-color">
                  Category Text Color
                </label>
                <div className="cg-color-field">
                  <input
                    id="faq-text-color"
                    type="color"
                    value={form.categoryTextColor}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, categoryTextColor: e.target.value }))
                    }
                  />
                  <span>{form.categoryTextColor}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="cg-field-label" htmlFor="faq-question-input">
              Question
            </label>
            <input
              id="faq-question-input"
              className="cg-input"
              maxLength={300}
              placeholder="e.g., How do I track my order status in real-time?"
              value={form.question}
              onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            />
          </div>

          <div>
            <label className="cg-field-label" htmlFor="faq-answer-input">
              Answer
            </label>
            <textarea
              id="faq-answer-input"
              className="cg-textarea"
              rows={5}
              maxLength={2000}
              placeholder="Provide a helpful and clear answer for your customers..."
              value={form.answer}
              onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
            />
          </div>
        </div>

        <div className="cg-modal-foot">
          <button type="button" className="cg-btn-text" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="cg-btn-pill"
            disabled={saving}
            onClick={() => void onSubmit()}
          >
            {saving ? 'Saving…' : 'Save Question'}
          </button>
        </div>
      </div>
    </div>
  );
}
